import { Box, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { SectionLabel, PillButton } from '@denise-augusta/ui';
import { useSiteContent } from '@/providers/SiteContentProvider';
import { SiteFooter } from '@/layouts/PublicLayout';
import { RichText } from '@/components/RichText';
import { isRichTextEmpty } from '@/lib/rich-text';
import type { PricingTier } from '@denise-augusta/domain';

const CIRCLES_BG = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 1600'><g fill='none' stroke='%231F2D5C' stroke-opacity='0.22' stroke-width='1.4'><circle cx='160' cy='190' r='90'/><circle cx='980' cy='240' r='170'/><circle cx='500' cy='620' r='110'/><circle cx='1300' cy='780' r='150'/><circle cx='270' cy='1010' r='210'/><circle cx='1150' cy='1240' r='120'/><circle cx='720' cy='1380' r='80'/><path d='M 0,420 Q 300,320 620,420 T 1240,420 T 1600,420'/><path d='M 0,1120 Q 400,1020 820,1120 T 1600,1120'/><path d='M 220,210 L 360,310'/><path d='M 870,1320 L 1020,1420'/><path d='M 1100,80 L 1240,180'/></g></svg>")`;

/**
 * Legacy .p-title treatment: the last word of the card title is italic
 * periwinkle ("How Engagements <em>Work</em>").
 */
function AccentedTitle({ title }: { title: string }) {
  const words = title.trim().split(' ');
  if (words.length < 2) return <>{title}</>;
  const last = words.pop();
  return (
    <>
      {words.join(' ')}{' '}
      <Box as="em" fontStyle="italic" color="brand.mid">
        {last}
      </Box>
    </>
  );
}

function TierCard({ tier }: { tier: PricingTier }) {
  return (
    <Flex
      direction="column"
      bg="paper.white"
      border="1px solid"
      borderColor="paper.border"
      p="2.8rem 2.2rem"
      borderRadius="20px"
      position="relative"
      overflow="hidden"
      transition="transform 0.28s, box-shadow 0.28s"
      boxShadow={tier.featured ? '0 14px 44px rgba(61,82,168,0.08)' : undefined}
      _hover={{ transform: 'translateY(-5px)', boxShadow: '0 20px 60px rgba(61,82,168,0.12)' }}
      css={{
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: '#3D52A8',
          transform: 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 0.35s',
        },
        '&:hover::before': { transform: 'scaleX(1)' },
      }}
    >
      <Heading as="h3" fontFamily="heading" fontSize="2.4rem" fontWeight="300" color="brand.ink" lineHeight="1.1" mb="0.3rem">
        <Box as="em" fontStyle="italic" color="brand.mid">
          {tier.name}
        </Box>
      </Heading>
      <Text fontFamily="heading" fontSize="0.95rem" fontWeight="300" color="ink.body" mb="1.2rem" letterSpacing="0.04em">
        {tier.subtitle}
      </Text>
      <Flex align="baseline" gap="0.35rem" wrap="wrap" mb="1.3rem" pb="1.3rem" borderBottom="1px solid" borderBottomColor="paper.linen">
        <Text w="100%" fontSize="0.68rem" fontWeight="500" letterSpacing="0.12em" color="ink.muted" mb="0.1rem">
          starting at
        </Text>
        <Text fontFamily="heading" fontSize="2.6rem" fontWeight="300" color="brand.ink" lineHeight="1">
          {tier.startingAt}
        </Text>
        <Text fontSize="0.88rem" color="ink.muted">
          {tier.period}
        </Text>
      </Flex>
      <RichText html={tier.description} fontSize="0.9rem" color="ink.muted" lineHeight="1.72" mb="2.5rem" flex="1" />
      <PillButton asChild scheme="solid" display="block" textAlign="center">
        <RouterLink to="/contact">Get Started</RouterLink>
      </PillButton>
    </Flex>
  );
}

export function Pricing() {
  const content = useSiteContent();

  return (
    <Box
      css={{
        backgroundColor: '#F5F4F1',
        backgroundImage: CIRCLES_BG,
        backgroundRepeat: 'repeat',
        backgroundSize: '1500px 1500px',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* PAGE HEADER */}
      <Box
        pt={{ base: '11.25rem', md: '11.25rem' }}
        pb="3rem"
        px={{ base: '1.8rem', md: '3.5rem' }}
        bg="brand.wash"
        textAlign="center"
        position="relative"
        overflow="hidden"
        borderBottom="2.5px solid"
        borderBottomColor="brand.lime"
        _before={{
          content: '""',
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          w: '500px',
          h: '500px',
          borderRadius: '50%',
          border: '1px solid rgba(61,82,168,0.10)',
          pointerEvents: 'none',
        }}
      >
        <SectionLabel centered display>How I Work</SectionLabel>
        <Heading
          as="h1"
          fontFamily="heading"
          fontSize="clamp(3rem, 6.5vw, 6rem)"
          fontWeight="300"
          lineHeight="0.95"
          color="brand.ink"
          mb="1.5rem"
          position="relative"
          zIndex="1"
        >
          Partnership,
          <br />
          <Box as="em" fontStyle="italic" color="brand.mid">
            not punch-cards
          </Box>
        </Heading>
        <Text
          fontFamily="heading"
          fontStyle="italic"
          fontSize="1.28rem"
          color="ink.soft"
          maxW="600px"
          mx="auto"
          lineHeight="1.65"
          position="relative"
          zIndex="1"
        >
          Every engagement is designed around your priorities, pace, and working style — not the clock.
        </Text>
      </Box>

      {/* TIERS */}
      <Box maxW="1200px" mx="auto" px={{ base: '1.8rem', md: '3.5rem' }} pt={{ base: '3.5rem', md: '5rem' }} pb="1.5rem">
        <Box textAlign="center" mb="3rem">
          <SectionLabel centered>Plans</SectionLabel>
          <Heading as="h2" fontFamily="heading" fontSize="clamp(2.3rem, 4.5vw, 3.4rem)" fontWeight="300" color="brand.ink" lineHeight="1.05" mb="0.9rem">
            Choose Your{' '}
            <Box as="em" fontStyle="italic" color="brand.mid">
              Level
            </Box>
          </Heading>
          <Text fontFamily="heading" fontStyle="italic" fontSize="1.05rem" color="ink.soft" maxW="620px" mx="auto" lineHeight="1.65">
            Two flexible tiers designed around how you work. Let's begin where you need the most support — and adjust as priorities evolve.
          </Text>
        </Box>
        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(auto-fit, minmax(280px, 320px))' }}
          gap="1.5rem"
          alignItems="stretch"
          justifyContent="center"
        >
          {content.tiers.filter((tier) => !tier.hidden).map((tier) => (
            <TierCard key={tier.name} tier={tier} />
          ))}
        </Grid>
      </Box>

      {/* ADD-ON */}
      <Box maxW="1200px" mx="auto" px={{ base: '1.8rem', md: '3.5rem' }} pt="0.5rem" pb="2rem">
        <Grid
          bg="rgba(234,237,245,0.55)"
          border="1px solid"
          borderColor="brand.pale"
          borderRadius="20px"
          p={{ base: '2.2rem 1.8rem', md: '2.8rem' }}
          templateColumns={{ base: '1fr', md: '1fr 1.4fr' }}
          gap={{ base: '1.5rem', md: '3rem' }}
          backdropFilter="blur(4px)"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            h: '3px',
            bg: 'brand.lime',
            borderRadius: '20px 20px 0 0',
          }}
        >
          <Flex direction="column">
            <SectionLabel>Add-On</SectionLabel>
            <Heading as="h3" fontFamily="heading" fontSize="1.85rem" fontWeight="400" color="brand.ink" lineHeight="1.12" mb="0.5rem">
              Personal Administrative{' '}
              <Box as="em" fontStyle="italic" color="brand.mid">
                Support
              </Box>
            </Heading>
            <Text fontFamily="heading" fontStyle="italic" fontSize="0.95rem" color="ink.soft" mb="1.2rem">
              {content.addOn.availability}
            </Text>
            <Flex align="baseline" gap="0.35rem" wrap="wrap" mb="1.2rem" pb="1.2rem" borderBottom="1px solid rgba(61,82,168,0.18)">
              <Text w="100%" fontSize="0.68rem" fontWeight="500" letterSpacing="0.12em" color="ink.muted">
                starting at
              </Text>
              <Text fontFamily="heading" fontSize="2.6rem" fontWeight="300" color="brand.ink" lineHeight="1">
                {content.addOn.startingAt}
              </Text>
              <Text fontSize="0.88rem" color="ink.muted">
                {content.addOn.period}
              </Text>
            </Flex>
            <RichText html={content.addOn.description} fontSize="0.9rem" color="ink.soft" lineHeight="1.72" />
          </Flex>
          <Flex direction="column">
            <Text fontSize="0.7rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.mid" mb="0.8rem">
              What It Covers
            </Text>
            <RichText html={content.addOn.coverage} fontSize="0.9rem" color="ink.soft" lineHeight="1.72" mb="1.4rem" />
            <RichText
              html={content.addOn.finePrint}
              fontSize="0.78rem"
              color="ink.muted"
              lineHeight="1.7"
              fontStyle="italic"
              borderTop="1px solid rgba(61,82,168,0.18)"
              pt="1.1rem"
              mt="auto"
            />
          </Flex>
        </Grid>
      </Box>

      {/* TIER NOTE — boxed aside between the add-on and Engagement (legacy .tier-note) */}
      {!isRichTextEmpty(content.tierNote) && (
        <RichText
          html={content.tierNote}
          maxW="860px"
          mx="auto"
          mt="1rem"
          mb="2.5rem"
          px={{ base: '1.8rem', md: '2rem' }}
          py="1.4rem"
          fontFamily="heading"
          fontStyle="italic"
          fontSize="1.05rem"
          color="ink.body"
          lineHeight="1.75"
          textAlign="center"
          bg="brand.wash"
          borderRadius="12px"
          border="1px solid"
          borderColor="brand.pale"
        />
      )}

      {/* WHAT TO EXPECT */}
      <Box as="main" maxW="1160px" mx="auto" px={{ base: '1.8rem', md: '3.5rem' }} pt={{ base: '3.5rem', md: '5rem' }} pb={{ base: '4rem', md: '6rem' }}>
        <Box textAlign="center" mb="3rem">
          <SectionLabel centered>Engagement</SectionLabel>
          <Heading as="h2" fontFamily="heading" fontSize="clamp(2.3rem, 4.5vw, 3.4rem)" fontWeight="300" color="brand.ink" lineHeight="1.05">
            What to{' '}
            <Box as="em" fontStyle="italic" color="brand.mid">
              Expect
            </Box>
          </Heading>
        </Box>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap="1.5rem" mb="2.5rem">
          {content.expect.map((card) => (
            <Flex
              key={card.title}
              direction="column"
              bg="paper.white"
              border="1px solid"
              borderColor="paper.border"
              p="3.2rem 2.8rem"
              borderRadius="20px"
              position="relative"
              overflow="hidden"
              transition="transform 0.28s, box-shadow 0.28s"
              _hover={{ transform: 'translateY(-5px)', boxShadow: '0 20px 60px rgba(61,82,168,0.12)' }}
              css={{
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: '#3D52A8',
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.35s',
                },
                '&:hover::before': { transform: 'scaleX(1)' },
              }}
            >
              <Heading as="h3" fontFamily="heading" fontSize="2.15rem" fontWeight="400" color="brand.ink" lineHeight="1.15" mb="1rem">
                <AccentedTitle title={card.title} />
              </Heading>
              {card.paragraphs.map((paragraph) => (
                <RichText key={paragraph.slice(0, 24)} html={paragraph} fontSize="1.05rem" color="ink.soft" lineHeight="1.78" mb="1.1rem" />
              ))}
              {card.bullets && (
                <Box as="ul" listStyleType="none" mt="0.2rem">
                  {card.bullets.map((bullet) => (
                    <Flex as="li" key={bullet} fontSize="1rem" color="ink.soft" py="0.32rem" align="flex-start" gap="0.75rem" lineHeight="1.55">
                      <Box as="span" color="brand.mid" flexShrink="0" fontWeight="600" fontSize="1.15rem" lineHeight="1" mt="0.15rem">
                        •
                      </Box>
                      {bullet}
                    </Flex>
                  ))}
                </Box>
              )}
            </Flex>
          ))}
        </Grid>
        <Box maxW="920px" mx="auto" mt="3rem" textAlign="center" bg="brand.wash" p={{ base: '2rem', md: '2.6rem 3rem' }} borderRadius="16px" border="1px solid" borderColor="brand.pale">
          <Heading as="h3" fontFamily="heading" fontSize="2rem" fontWeight="400" color="brand.ink" mb="1.1rem">
            {content.contractBasics.heading}
          </Heading>
          {content.contractBasics.paragraphs.map((paragraph) => (
            <RichText
              key={paragraph.slice(0, 24)}
              html={paragraph}
              fontFamily="heading"
              fontStyle="italic"
              fontSize="1.32rem"
              color="ink.soft"
              lineHeight="1.8"
              mb="1rem"
              _last={{ mb: 0 }}
            />
          ))}
        </Box>
      </Box>

      {/* CTA */}
      <Box bg="brand.wash" px={{ base: '1.8rem', md: '3.5rem' }} py={{ base: '3.5rem', md: '2.8rem' }} textAlign="center" borderTop="2.5px solid" borderTopColor="brand.lime">
        <Heading as="h2" fontFamily="heading" fontSize="clamp(2.2rem, 4vw, 3.5rem)" fontWeight="300" color="brand.ink" mb="0.7rem">
          Ready to find{' '}
          <Box as="em" fontStyle="italic" color="brand.mid">
            the right fit?
          </Box>
        </Heading>
        <Text fontFamily="heading" fontStyle="italic" fontSize="1.2rem" color="ink.soft" mb="2.2rem">
          Let's talk about your priorities and where I can make the biggest difference.
        </Text>
        <PillButton asChild scheme="plain" px="2.8rem" py="1rem">
          <RouterLink to="/contact">Start the Conversation →</RouterLink>
        </PillButton>
      </Box>

      <SiteFooter copyright={content.footer.copyright} />
    </Box>
  );
}
